// Location Detection Module for Force USA Theme
class LocationDetector {
  constructor() {
    this.apiEndpoint =
      "https://ofr0nb6t91.execute-api.us-east-1.amazonaws.com/dev/location?top-secret-code=VWjjcyXvKSbJa9XvrvTajWVr5Kw8ZJMdYT";
    this.locationCookieName = "user_location";
    this.choiceCookieName = "user_location_choice";
    this.cookieExpireDays = 30;
    this.allowedCountries = ["US", "CA"]; // United States and Canada
    this.modalShown = false;

    this.init();
  }

  init() {
    // Check if user has already made a choice
    const userChoice = this.getChoiceCookie();
    if (userChoice) {
      console.log("User location choice already recorded:", userChoice);
      return; // Don't show modal again
    }

    // Check if location is already stored in cookie
    const savedLocation = this.getLocationCookie();

    if (savedLocation) {
      this.handleLocationData(savedLocation);
    } else {
      // Fetch location from AWS endpoint
      this.fetchLocation();
    }
  }

  async fetchLocation() {
    try {
      const response = await fetch(this.apiEndpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const locationData = await response.json();

      // Save to location cookie
      this.setLocationCookie(locationData);

      // Handle the location
      this.handleLocationData(locationData);
    } catch (error) {
      console.error("Error fetching location:", error);
      // Assume allowed location if API fails
      this.handleLocationData({ country: "US" });
    }
  }

  handleLocationData(locationData) {
    const countryCode =
      locationData.country_code || locationData.country || "US";

    // Check if user is outside allowed countries
    if (
      !this.allowedCountries.includes(countryCode.toUpperCase()) &&
      !this.modalShown
    ) {
      this.showLocationModal(countryCode, locationData);
    }
  }

  showLocationModal(countryCode, locationData = {}) {
    this.modalShown = true;

    // Get readable location info
    const countryName = locationData.country_name || countryCode;
    const city = locationData.city || "";
    const region = locationData.region_name || "";

    let locationString = countryName;
    if (city && region) {
      locationString = `${city}, ${region}, ${countryName}`;
    } else if (city) {
      locationString = `${city}, ${countryName}`;
    } else if (region) {
      locationString = `${region}, ${countryName}`;
    }

    // Create modal HTML
    const modalHTML = `
      <div id="location-modal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 opacity-0 pointer-events-none transition-opacity duration-300">
        <div class="bg-white rounded-lg p-8 max-w-md w-full mx-4 transform scale-95 transition-transform duration-300">
          <div class="text-center">
            <h3 class="text-xl font-bold mb-4">International Customer?</h3>
            <p class="text-gray-600 mb-6">
              We've detected that you're browsing from outside the US and Canada. 
              For the best shopping experience and local support, please visit our international locations page.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <button 
                onclick="handleLocationChoice('continue')" 
                class="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Continue Shopping
              </button>
              <a 
                href="/pages/locations" 
                onclick="handleLocationChoice('redirect')"
                class="flex-1 px-4 py-2 bg-secondary text-white text-center rounded hover:bg-secondary/90 transition-colors"
              >
                View Locations
              </a>
            </div>
            <p class="text-xs text-gray-500 mt-4">
              Detected location: ${locationString}
            </p>
          </div>
        </div>
      </div>
    `;

    // Remove existing modal if present
    const existingModal = document.getElementById("location-modal");
    if (existingModal) {
      existingModal.remove();
    }

    // Add modal to page
    document.body.insertAdjacentHTML("beforeend", modalHTML);

    // Show modal with animation
    setTimeout(() => {
      const modal = document.getElementById("location-modal");
      if (modal) {
        const content = modal.querySelector(".bg-white");
        modal.classList.remove("opacity-0", "pointer-events-none");
        content.classList.remove("scale-95");
      }
    }, 100);

    // Close modal when clicking outside
    const modal = document.getElementById("location-modal");
    if (modal) {
      modal.addEventListener("click", (e) => {
        if (e.target === modal) {
          this.closeModal();
        }
      });

      // Close modal on escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && modal) {
          this.closeModal();
        }
      });
    }
  }

  handleUserChoice(choice) {
    // Set choice cookie for 30 days
    this.setChoiceCookie(choice);

    // Close modal
    this.closeModal();

    console.log("User location choice recorded:", choice);
  }

  closeModal() {
    const modal = document.getElementById("location-modal");
    if (modal) {
      const content = modal.querySelector(".bg-white");
      modal.classList.add("opacity-0", "pointer-events-none");
      content.classList.add("scale-95");

      setTimeout(() => {
        modal.remove();
      }, 300);
    }
  }

  setLocationCookie(locationData) {
    const expires = new Date();
    expires.setTime(
      expires.getTime() + this.cookieExpireDays * 24 * 60 * 60 * 1000
    );

    const cookieValue = JSON.stringify({
      country_code: locationData.country_code || locationData.country || "US",
      country_name: locationData.country_name || "",
      city: locationData.city || "",
      region_name: locationData.region_name || "",
      ip: locationData.ip || "",
      timestamp: new Date().getTime(),
    });

    document.cookie = `${this.locationCookieName}=${encodeURIComponent(
      cookieValue
    )};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  }

  setChoiceCookie(choice) {
    const expires = new Date();
    expires.setTime(
      expires.getTime() + this.cookieExpireDays * 24 * 60 * 60 * 1000
    );

    const cookieValue = JSON.stringify({
      choice: choice,
      timestamp: new Date().getTime(),
    });

    document.cookie = `${this.choiceCookieName}=${encodeURIComponent(
      cookieValue
    )};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
  }

  getLocationCookie() {
    const nameEQ = this.locationCookieName + "=";
    const ca = document.cookie.split(";");

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        try {
          const cookieValue = decodeURIComponent(
            c.substring(nameEQ.length, c.length)
          );
          return JSON.parse(cookieValue);
        } catch (e) {
          console.error("Error parsing location cookie:", e);
          return null;
        }
      }
    }
    return null;
  }

  getChoiceCookie() {
    const nameEQ = this.choiceCookieName + "=";
    const ca = document.cookie.split(";");

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        try {
          const cookieValue = decodeURIComponent(
            c.substring(nameEQ.length, c.length)
          );
          return JSON.parse(cookieValue);
        } catch (e) {
          console.error("Error parsing choice cookie:", e);
          return null;
        }
      }
    }
    return null;
  }

  // Public methods to clear cookies (for testing)
  clearLocationCookie() {
    document.cookie = `${this.locationCookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    console.log("Location cookie cleared");
  }

  clearChoiceCookie() {
    document.cookie = `${this.choiceCookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    console.log("Choice cookie cleared");
  }

  clearAllCookies() {
    this.clearLocationCookie();
    this.clearChoiceCookie();
    console.log("All location cookies cleared");
  }
}

// Global function to handle user choice (called from HTML)
function handleLocationChoice(choice) {
  if (window.locationDetector) {
    window.locationDetector.handleUserChoice(choice);
  }
}

// Global function to close modal (called from HTML)
function closeLocationModal() {
  if (window.locationDetector) {
    window.locationDetector.closeModal();
  }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  // Initialize location detector
  window.locationDetector = new LocationDetector();

  // Make functions globally accessible for testing
  window.clearLocationCookie = () => {
    if (window.locationDetector) {
      window.locationDetector.clearLocationCookie();
    }
  };

  window.clearChoiceCookie = () => {
    if (window.locationDetector) {
      window.locationDetector.clearChoiceCookie();
    }
  };

  window.clearAllLocationCookies = () => {
    if (window.locationDetector) {
      window.locationDetector.clearAllCookies();
    }
  };
});

// Make classes globally accessible
window.LocationDetector = LocationDetector;
window.handleLocationChoice = handleLocationChoice;
window.closeLocationModal = closeLocationModal;
